import { AxiosPromise, AxiosResponse } from 'axios';
import { useDispatch } from 'react-redux';
import { finishLoading, startLoading } from '../../store/reducers/Loading';
import { handleError } from '../../helpers/hooks/handleError';
import { useState } from 'react';
import React from 'react';

function makeBackgroundWrapper<T>(
    promise: () => AxiosPromise<T>,
    dispatch: React.Dispatch<any>,
    onLoading: (x: boolean) => void,
    afterCall?: (T) => void,
    onError?: (e) => void,
    disableAnimation?: boolean,
): Promise<void> {
    const wrappedPromise: () => Promise<void> = async () => {
        let response: AxiosResponse | null = null;
        try {
            if (!disableAnimation) dispatch(startLoading);
            onLoading(true);
            response = await promise();
        } catch (error) {
            handleError(error, dispatch);
            if (onError) onError(error);
        } finally {
            if (!disableAnimation) dispatch(finishLoading);
            onLoading(false);
        }
        if (response !== null && afterCall !== undefined) afterCall(response.data);
    };

    return wrappedPromise();
}

export const useBackgroundPromiseWrapper = <T>(): [
    (
        call: () => AxiosPromise<T>,
        afterCall?: ((T: any) => void) | undefined,
        onError?: ((e) => void) | undefined,
        disableAnimation?: boolean | undefined,
        useBlockedLoading?: boolean | undefined,
    ) => void,
    boolean,
] => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onLoading = React.useCallback((loading: boolean) => setIsLoading(loading), []);

    const promise = React.useCallback(
        (call: () => AxiosPromise<T>, afterCall?: (T) => void, onError?: (e) => void, disableAnimation?: boolean) => {
            makeBackgroundWrapper<T>(call, dispatch, onLoading, afterCall, onError, disableAnimation);
        },
        [dispatch, onLoading],
    );

    return [promise, isLoading];
};

export const useBackgroundPromiseAllWrapper = <T extends any[]>(): [
    (
        calls: (() => AxiosPromise<any>)[],
        afterCall?: ((results: T) => void) | undefined,
        onError?: ((e: any) => void) | undefined,
        disableAnimation?: boolean | undefined,
        useBlockedLoading?: boolean | undefined,
    ) => void,
    boolean,
] => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const onLoading = React.useCallback((loading: boolean) => setIsLoading(loading), []);

    const promiseAll = React.useCallback(
        (
            calls: (() => AxiosPromise<any>)[],
            afterCall?: (results: T) => void,
            onError?: (e: any) => void,
            disableAnimation?: boolean,
        ) => {
            makeBackgroundWrapper<T>(
                () =>
                    Promise.allSettled(calls.map((call) => call())).then((results) => {
                        const errors = results
                            .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
                            .map((r) => r.reason);

                        if (errors.length > 0) {
                            errors.forEach((e) => onError?.(e));
                        }

                        return {
                            data: results.map((r) => (r.status === 'fulfilled' ? r.value.data : null)),
                        };
                    }) as any,
                dispatch,
                onLoading,
                afterCall as any,
                onError,
                disableAnimation,
            );
        },
        [dispatch, onLoading],
    );

    return [promiseAll, isLoading];
};
